import { FormEvent, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { cn } from '../../lib/cn';
import { isRecaptchaConfigured, requestRecaptchaToken } from '../../lib/recaptcha';
import { hudCopy } from '../content';
import { HudPanel } from './HudPanel';
import { ScannerOverlay } from './ScannerOverlay';
import { StatusChip } from './StatusChip';
import { SystemButton } from './SystemButton';
import { SystemHeader } from './SystemHeader';

type FieldKey = 'name' | 'email' | 'company' | 'message';

type ContactFields = {
  name: string;
  email: string;
  company: string;
  message: string;
  website: string;
};

type ContactSubmission = ContactFields & {
  recaptchaToken?: string;
};

type FieldErrors = Partial<Record<FieldKey, string>>;
type TouchMap = Partial<Record<FieldKey, boolean>>;

type TerminalState = 'idle' | 'sending' | 'success' | 'error';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_TOKEN_REGEX = /\b(?:https?:\/\/|www\.)\S+/i;

const fieldLabels: Record<FieldKey, string> = {
  name: 'Operator Name',
  email: 'Business Channel',
  company: 'Organization',
  message: 'Mission Brief'
};

const getClientErrors = (fields: ContactFields): FieldErrors => {
  const errors: FieldErrors = {};

  if (!fields.name.trim() || fields.name.trim().length < 2) {
    errors.name = 'Enter your full name.';
  } else if (URL_TOKEN_REGEX.test(fields.name)) {
    errors.name = 'Name contains unsupported content.';
  }

  if (!fields.email.trim() || !EMAIL_REGEX.test(fields.email.trim())) {
    errors.email = 'Enter a valid business email.';
  }

  if (!fields.company.trim() || fields.company.trim().length < 2) {
    errors.company = 'Enter your company name.';
  } else if (URL_TOKEN_REGEX.test(fields.company)) {
    errors.company = 'Company contains unsupported content.';
  }

  if (!fields.message.trim() || fields.message.trim().length < 10) {
    errors.message = 'Provide a concise project brief (minimum 10 characters).';
  }

  return errors;
};

export const TerminalForm = () => {
  const reducedMotion = useReducedMotion();
  const [fields, setFields] = useState<ContactFields>({
    name: '',
    email: '',
    company: '',
    message: '',
    website: ''
  });
  const [status, setStatus] = useState<TerminalState>('idle');
  const [feedback, setFeedback] = useState<string>('Terminal ready for secure transmission.');
  const [touched, setTouched] = useState<TouchMap>({});
  const [focusedField, setFocusedField] = useState<FieldKey | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});

  const clientErrors = useMemo(() => getClientErrors(fields), [fields]);
  const mergedErrors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);
  const canSubmit = useMemo(() => Object.keys(clientErrors).length === 0, [clientErrors]);

  const setFieldValue = (key: FieldKey, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setServerErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (status !== 'idle') {
      setStatus('idle');
      setFeedback('Terminal ready for secure transmission.');
    }
  };

  const handleBlur = (key: FieldKey) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
    setFocusedField((prev) => (prev === key ? null : prev));
  };

  const fieldHasValue = (key: FieldKey): boolean => Boolean(fields[key].trim());
  const shouldShowError = (key: FieldKey): boolean => Boolean(mergedErrors[key] && (submitted || touched[key]));

  const fieldState = (key: FieldKey): 'idle' | 'valid' | 'error' => {
    if (shouldShowError(key)) {
      return 'error';
    }
    if (fieldHasValue(key) && !mergedErrors[key]) {
      return 'valid';
    }
    return 'idle';
  };

  const parseServerErrors = (payload: unknown): FieldErrors => {
    if (!payload || typeof payload !== 'object') {
      return {};
    }

    const errorBag =
      (payload as { errors?: unknown; fieldErrors?: unknown }).errors ??
      (payload as { errors?: unknown; fieldErrors?: unknown }).fieldErrors;

    if (!errorBag || typeof errorBag !== 'object') {
      return {};
    }

    const normalized: FieldErrors = {};
    (['name', 'email', 'company', 'message'] as FieldKey[]).forEach((field) => {
      const value = (errorBag as Record<string, unknown>)[field];
      if (typeof value === 'string' && value.trim()) {
        normalized[field] = value;
      }
    });

    return normalized;
  };

  const parseServerMessage = (payload: unknown): string | null => {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const message =
      (payload as { message?: unknown; error?: unknown }).message ??
      (payload as { message?: unknown; error?: unknown }).error;

    if (typeof message !== 'string') {
      return null;
    }

    const normalizedMessage = message.trim();
    return normalizedMessage || null;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (!canSubmit) {
      setStatus('error');
      setFeedback('Transmission blocked. Review highlighted fields.');
      return;
    }

    setStatus('sending');
    setFeedback('Transmitting request packet...');

    try {
      let recaptchaToken: string | undefined;
      if (isRecaptchaConfigured()) {
        try {
          recaptchaToken = await requestRecaptchaToken();
        } catch {
          setStatus('error');
          setFeedback('Security verification unavailable. Disable blockers and retry.');
          return;
        }
      }

      const payload: ContactSubmission = recaptchaToken ? { ...fields, recaptchaToken } : fields;

      const response = await fetch(hudCopy.contact.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const parsedErrors = parseServerErrors(payload);
        const serverMessage = parseServerMessage(payload);
        if (Object.keys(parsedErrors).length > 0) {
          setServerErrors(parsedErrors);
          setTouched({ name: true, email: true, company: true, message: true });
        }

        setStatus('error');
        setFeedback(
          Object.keys(parsedErrors).length > 0
            ? 'Transmission fault detected. Correct flagged fields and retry.'
            : serverMessage || 'Transmission failed. Retry or email info@develogic-digital.com.'
        );
        return;
      }

      setStatus('success');
      setFeedback('Channel acknowledged. Request queued. DeveLogic will respond within one business day.');
      setFields({ name: '', email: '', company: '', message: '', website: '' });
      setTouched({});
      setSubmitted(false);
      setServerErrors({});
    } catch {
      setStatus('error');
      setFeedback('Connection unstable. Re-establish link and retry transmission.');
    }
  };

  const validationSummary = `${Object.keys(mergedErrors).length} flag${Object.keys(mergedErrors).length === 1 ? '' : 's'}`;

  return (
    <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
      <HudPanel pulse className="relative">
        <ScannerOverlay tone="normal" />
        <SystemHeader
          index="SCREEN 06"
          title={hudCopy.contact.heading}
          subtitle={hudCopy.contact.subtitle}
          tag="SECURE CHANNEL"
          status={hudCopy.microLabels.routeStable}
        />
        <p className="mt-4 text-sm text-brand-text-2">{hudCopy.contact.body}</p>

        <div className="mt-6 space-y-3">
          {[
            'Business-first project discovery',
            'Confidential and secure handling',
            'Technical response within one business day'
          ].map((item) => (
            <div key={item} className="rounded-lg border border-brand-lime/20 bg-brand-panel-2/65 px-3 py-2 text-sm text-brand-text-2">
              {item}
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-lg border border-brand-lime/25 bg-brand-panel-2/70 p-3">
          <div className="flex items-center justify-between gap-3">
            <p className="hud-micro text-brand-lime">Link Diagnostics</p>
            <StatusChip label={hudCopy.microLabels.signal} />
          </div>
          <div className="mt-3 grid grid-cols-8 gap-1">
            {Array.from({ length: 8 }).map((_, index) => (
              <motion.span
                key={index}
                className="h-9 rounded-sm bg-[linear-gradient(180deg,rgba(183,255,106,0.8),rgba(88,247,182,0.22))]"
                animate={
                  reducedMotion
                    ? undefined
                    : {
                        opacity: [0.35, 1, 0.4],
                        scaleY: [0.45, 1, 0.6]
                      }
                }
                transition={{ duration: 1.8, repeat: Infinity, delay: index * 0.12 }}
              />
            ))}
          </div>
        </div>
      </HudPanel>

      <HudPanel as="form" pulse className="relative" onSubmit={submit}>
        <ScannerOverlay tone="intense" />

        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="hud-micro text-brand-lime">Transmission Interface</p>
          <span
            className={cn(
              'hud-micro rounded-md border px-2 py-1',
              status === 'success'
                ? 'border-brand-lime/55 text-brand-lime'
                : status === 'error'
                  ? 'border-rose-400/55 text-rose-300'
                  : 'border-brand-aqua/55 text-brand-aqua'
            )}
          >
            {status === 'success'
              ? 'CHANNEL ACKNOWLEDGED'
              : status === 'error'
                ? `REVIEW REQUIRED // ${validationSummary}`
                : status === 'sending'
                  ? 'TRANSMITTING'
                  : 'READY'}
          </span>
        </div>

        <div className="grid gap-3">
          {(
            [
              { key: 'name', placeholder: 'Full Name' },
              { key: 'email', placeholder: 'Business Email' },
              { key: 'company', placeholder: 'Company' }
            ] as Array<{ key: FieldKey; placeholder: string }>
          ).map((field) => {
            const state = fieldState(field.key);
            const focused = focusedField === field.key;

            return (
              <motion.div
                key={field.key}
                className="rounded-xl border bg-brand-panel-2/55 p-3"
                animate={
                  reducedMotion
                    ? undefined
                    : {
                        borderColor:
                          state === 'error'
                            ? 'rgba(251,113,133,0.65)'
                            : focused
                              ? 'rgba(183,255,106,0.85)'
                              : state === 'valid'
                                ? 'rgba(88,247,182,0.7)'
                                : 'rgba(183,255,106,0.28)',
                        boxShadow:
                          focused && state !== 'error'
                            ? '0 0 20px rgba(183,255,106,0.18)'
                            : state === 'error'
                              ? '0 0 16px rgba(251,113,133,0.16)'
                              : '0 0 0 rgba(0,0,0,0)'
                      }
                }
                transition={{ duration: 0.24 }}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label htmlFor={`terminal-${field.key}`} className="hud-micro text-brand-text-2">
                    {fieldLabels[field.key]}
                  </label>
                  <span
                    className={cn(
                      'hud-micro text-[0.58rem]',
                      state === 'error' ? 'text-rose-300' : state === 'valid' ? 'text-brand-aqua' : 'text-brand-text-3'
                    )}
                  >
                    {state === 'error' ? 'FLAGGED' : state === 'valid' ? 'VERIFIED' : 'PENDING'}
                  </span>
                </div>

                <input
                  id={`terminal-${field.key}`}
                  className="hud-input"
                  type={field.key === 'email' ? 'email' : 'text'}
                  placeholder={field.placeholder}
                  value={fields[field.key]}
                  onFocus={() => setFocusedField(field.key)}
                  onBlur={() => handleBlur(field.key)}
                  onChange={(event) => setFieldValue(field.key, event.target.value)}
                  required
                />

                {shouldShowError(field.key) ? <p className="mt-2 text-xs text-rose-300">{mergedErrors[field.key]}</p> : null}
              </motion.div>
            );
          })}

          {(() => {
            const state = fieldState('message');
            const focused = focusedField === 'message';
            const charCount = fields.message.trim().length;

            return (
              <motion.div
                className="rounded-xl border bg-brand-panel-2/55 p-3"
                animate={
                  reducedMotion
                    ? undefined
                    : {
                        borderColor:
                          state === 'error'
                            ? 'rgba(251,113,133,0.65)'
                            : focused
                              ? 'rgba(183,255,106,0.85)'
                              : state === 'valid'
                                ? 'rgba(88,247,182,0.7)'
                                : 'rgba(183,255,106,0.28)',
                        boxShadow:
                          focused && state !== 'error'
                            ? '0 0 20px rgba(183,255,106,0.18)'
                            : state === 'error'
                              ? '0 0 16px rgba(251,113,133,0.16)'
                              : '0 0 0 rgba(0,0,0,0)'
                      }
                }
                transition={{ duration: 0.24 }}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label htmlFor="terminal-message" className="hud-micro text-brand-text-2">
                    {fieldLabels.message}
                  </label>
                  <span
                    className={cn(
                      'hud-micro text-[0.58rem]',
                      state === 'error' ? 'text-rose-300' : state === 'valid' ? 'text-brand-aqua' : 'text-brand-text-3'
                    )}
                  >
                    {state === 'error' ? 'FLAGGED' : state === 'valid' ? 'VERIFIED' : 'PENDING'}
                  </span>
                </div>

                <textarea
                  id="terminal-message"
                  className="hud-input min-h-40 resize-y"
                  placeholder="Project objectives, systems needed, and timeline"
                  value={fields.message}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => handleBlur('message')}
                  onChange={(event) => setFieldValue('message', event.target.value)}
                  required
                />

                <div className="mt-2 flex items-center justify-between gap-2">
                  {shouldShowError('message') ? <p className="text-xs text-rose-300">{mergedErrors.message}</p> : <span />}
                  <span className="hud-micro text-brand-text-3">{charCount} chars</span>
                </div>
              </motion.div>
            );
          })()}

          <input
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            name="website"
            value={fields.website}
            onChange={(event) => setFields((prev) => ({ ...prev, website: event.target.value }))}
          />
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <SystemButton type="submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Transmitting...' : 'Transmit Request'}
            </SystemButton>
            <SystemButton
              type="button"
              variant="secondary"
              onClick={() => {
                setFields({ name: '', email: '', company: '', message: '', website: '' });
                setTouched({});
                setSubmitted(false);
                setServerErrors({});
                setStatus('idle');
                setFeedback('Terminal buffer cleared. Ready for transmission.');
              }}
            >
              Clear Buffer
            </SystemButton>
          </div>

          <motion.p
            className={cn(
              'max-w-sm text-xs sm:text-sm',
              status === 'success' ? 'text-brand-lime' : status === 'error' ? 'text-rose-300' : 'text-brand-text-2'
            )}
            animate={status === 'sending' ? { opacity: [0.45, 1, 0.5] } : undefined}
            transition={{ duration: 1.2, repeat: Infinity }}
          >
            {feedback}
          </motion.p>
        </div>
      </HudPanel>
    </div>
  );
};
